import React from 'react';
import { Box, Typography } from '@mui/material';
import {
  DndContext,
  closestCenter,
  SensorDescriptor,
  SensorOptions,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { restrictToParentElement } from '@dnd-kit/modifiers';
import { ColumnConfigItem, CustomColumnDefinition } from '../../../types/lead';
import { SortableItem } from './SortableItem';

interface ReorderColumnsTabProps {
  sensors: SensorDescriptor<SensorOptions>[];
  columnsState: ColumnConfigItem[];
  customColumns: CustomColumnDefinition[];
  handleDragEnd: (event: DragEndEvent) => void;
  handleToggleVisibility: (key: string) => void;
  handleStartRename: (key: string) => void;
  handleInitiateDeactivate: (key: string) => void;
  editingKey: string | null;
  editingName: string;
  renameError: string | null;
  setEditingName: (val: string) => void;
  setRenameError: (val: string | null) => void;
  handleSaveRename: () => void;
  setEditingKey: (val: string | null) => void;
  highlightedKey: string | null;
  isChoiceDialogOpen?: boolean;
}

export const ReorderColumnsTab: React.FC<ReorderColumnsTabProps> = React.memo(({
  sensors,
  columnsState,
  customColumns,
  handleDragEnd,
  handleToggleVisibility,
  handleStartRename,
  handleInitiateDeactivate,
  editingKey,
  editingName,
  renameError,
  setEditingName,
  setRenameError,
  handleSaveRename,
  setEditingKey,
  highlightedKey,
  isChoiceDialogOpen = false,
}) => {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Drag columns to reorder. Toggle switches to show/hide columns on the Leads table.
      </Typography>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
        modifiers={[restrictToParentElement]}
        autoScroll={true}
      >
        <SortableContext items={columnsState.map((c) => c.key)} strategy={verticalListSortingStrategy}>
          <Box sx={{ pr: 0.5 }}>
            {columnsState.map((col) => (
              <SortableItem
                key={col.key}
                column={col}
                customColumns={customColumns}
                onToggleVisibility={handleToggleVisibility}
                onRename={handleStartRename}
                onDeactivate={handleInitiateDeactivate}
                isEditing={editingKey === col.key && !isChoiceDialogOpen}
                editingName={editingName}
                renameError={renameError}
                onEditingNameChange={(val) => {
                  setEditingName(val);
                  if (renameError) setRenameError(null);
                }}
                onSaveRename={handleSaveRename}
                onCancelRename={() => {
                  setEditingKey(null);
                  setRenameError(null);
                }}
                isHighlighted={col.key === highlightedKey}
              />
            ))}
          </Box>
        </SortableContext>
      </DndContext>
    </Box>
  );
});
